import React, { useCallback, useEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { Form, Spin } from 'antd'
import { groupBy } from 'lodash'
import PropTypes from 'prop-types'

import {
  AllergenFormListItem,
  DirtyPromptForm,
  FormButtons,
  FormListSelect,
} from '../../../common/components/sections'
import { StyledVerticalSpace } from '../../../common/components/styled'
import { PERMISSIONS } from '../../../common/constants'
import { showErrorNotification } from '../../../common/helpers'
import { useFormDirty, useUserHasPermissions } from '../../../common/hooks'
import {
  idPropType,
  optionPropType,
  simpleEntityPropType,
} from '../../../common/prop-types'

import { useUpdateDietExcludedAllergensDetailsMutation } from '../../hooks'
import { setDietPageIsDirty } from '../../redux/actions/diet-actions'

import { dietExcludedAllergensDetailsFormInitialValues } from './initial-values'

const DietExcludedAllergensDetailsForm = ({
  dietId,
  excludedAllergens,
  allergenOptions,
}) => {
  const dispatch = useDispatch()
  const [form] = Form.useForm()
  const { isDirty, setToDirty, resetForm } = useFormDirty(form)

  const isFormEnabled = useUserHasPermissions([PERMISSIONS.DIET.SAVE_ALLERGENS])

  const [
    updateDietExcludedAllergensDetails,
    { isLoading: isSaveLoading, error: saveError },
  ] = useUpdateDietExcludedAllergensDetailsMutation()

  const initialValues = useMemo(
    () => dietExcludedAllergensDetailsFormInitialValues(excludedAllergens),
    [excludedAllergens],
  )

  const allergenOptionsGroupedById = useMemo(
    () => groupBy(allergenOptions, 'value'),
    [allergenOptions],
  )

  const itemRender = useCallback(
    ({
      field: { key, name, fieldKey, ...restField },
      shouldModify,
      treeData,
      onClear,
      itemRules,
      ...rest
    }) => (
      <Form.Item
        key={key}
        name={name}
        fieldKey={fieldKey}
        rules={itemRules}
        noStyle
        {...restField}
      >
        <AllergenFormListItem
          shouldModify={shouldModify}
          name={name}
          treeData={treeData}
          onClear={onClear}
          allergenOptionsGroupedById={allergenOptionsGroupedById}
          {...rest}
        />
      </Form.Item>
    ),
    [allergenOptionsGroupedById],
  )

  const handleSaveData = useCallback(
    data => updateDietExcludedAllergensDetails(dietId, data),
    [dietId, updateDietExcludedAllergensDetails],
  )

  useEffect(() => {
    if (saveError) {
      showErrorNotification({
        title: 'Diet details error',
        description: saveError,
      })
    }
  }, [saveError])

  useEffect(() => {
    resetForm()
  }, [excludedAllergens, resetForm])

  useEffect(() => {
    dispatch(setDietPageIsDirty({ isExcludedAllergensDirty: isDirty }))
  }, [dispatch, isDirty])

  return isSaveLoading ? (
    <Spin />
  ) : (
    <DirtyPromptForm
      isDirty={isDirty}
      form={form}
      initialValues={initialValues}
      name="excludedAllergensForm"
      onFinish={handleSaveData}
      autoComplete="off"
      onFieldsChange={setToDirty}
    >
      <StyledVerticalSpace>
        <FormListSelect
          disabled={!isFormEnabled}
          form={form}
          labelAbove
          name="dietExcludedAllergenIds"
          label="Excluded Allergens"
          showCountItems
          showSearch
          options={allergenOptions}
          newItemSelectPlaceholder="Select a new allergen..."
          isDirty={isDirty}
          itemRender={itemRender}
        />
        <FormButtons
          permissions={[PERMISSIONS.DIET.SAVE_ALLERGENS]}
          isDisabled={!isDirty}
          onReset={resetForm}
        />
      </StyledVerticalSpace>
    </DirtyPromptForm>
  )
}

DietExcludedAllergensDetailsForm.propTypes = {
  dietId: idPropType.isRequired,
  excludedAllergens: PropTypes.arrayOf(simpleEntityPropType),
  allergenOptions: PropTypes.arrayOf(optionPropType.isRequired).isRequired,
}

DietExcludedAllergensDetailsForm.defaultProps = {
  excludedAllergens: [],
}

export default DietExcludedAllergensDetailsForm
